/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useSEOHead } from '../../../hooks/useSEOHead';
import { CORE_BUSINESS } from '../../../lib/coreSchemas';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import { ServiceAreasHero } from '../components/ServiceAreasHero';
import { NearbyAreasSection } from '../components/NearbyAreasSection';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../../../lib/schemaBuilder';

export default function AstoriaServiceAreaPage() {
  useSEOHead({
    title: 'Used Cars Near Astoria OR | No Dealer Fees, Free Delivery | Rockhound Auto',
    description: 'Shop quality used cars near Astoria, Oregon — 10 minutes up Hwy 101 in Warrenton. No dealer fees, coastal-inspected vehicles, free delivery to Astoria. Call today.',
    keywords: 'used cars near Astoria Oregon, Astoria used car dealer, no dealer fee cars Astoria OR, used vehicles Astoria Oregon, free delivery used cars Astoria',
    canonicalUrl: `${CORE_BUSINESS.url}/service-areas/astoria`,
    geoRegion: 'US-OR',
    geoPlacename: 'Astoria, Oregon',
    geoPosition: '46.1879;-123.8313',
  });

  const nearbyAreas = [
    { name: 'Warrenton', slug: 'warrenton' },
    { name: 'Seaside', slug: 'seaside' },
    { name: 'Cannon Beach', slug: 'cannon-beach' },
    { name: 'Gearhart', slug: 'gearhart' },
    { name: 'Long Beach WA', slug: 'long-beach-wa' },
    { name: 'Ilwaco WA', slug: 'ilwaco' },
  ];

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Service Areas',
          item: 'https://rockhoundauto.com/service-areas',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Astoria',
          item: 'https://rockhoundauto.com/service-areas/astoria',
        },
      ],
    };

    injectJsonLd('schema-astoria-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-astoria-breadcrumb');
    };
  }, []);

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Service Areas', path: '/service-areas' },
          { label: 'Astoria, Oregon' },
        ]}
      />

      <ServiceAreasHero
        city="Astoria"
        state="Oregon"
        description="We're 10 minutes up Highway 101 in Warrenton. Every vehicle is inspected for coastal durability — because 'low miles' means nothing if the undercarriage's already rusting out from salt air."
        distance="10 minutes from downtown"
      />

      <section aria-label="Why Astoria residents choose Rockhound Auto" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Astoria Residents Choose Rockhound
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Because we actually check for rust. And because we're close enough to visit but far enough that we don't pay downtown Astoria rent (which means better prices for you).
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Undercarriage Inspected</h3>
              <p className="text-slate-700">
                Every vehicle gets a full undercarriage inspection. If it spent winters near the ocean without proper care, we pass. You deserve a car that'll survive more than two rainy seasons.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">10 Minutes Up 101</h3>
              <p className="text-slate-700">
                Close enough for an easy visit, far enough that we don't pay downtown rent. Swing by after brunch at the Columbian or before hitting Fort Stevens.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-truck-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Free Delivery</h3>
              <p className="text-slate-700">
                Live in Astoria proper? We'll bring the car to you. Want to meet at the Astoria Column parking lot for a test drive with a view? We're game.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Local Knowledge Matters</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We're not some Portland dealer trying to unload inventory on coast folks. We live here. We shop at the same Safeway. We know what the salt air does to undercarriages, what the fog does to headlights, and what the Astoria-Megler Bridge wind does to your gas mileage.
            </p>
            <p className="text-slate-700 leading-relaxed">
              If we sell you a lemon, we'll run into you at the Saturday Market. Reputation matters when you're part of the community.
            </p>
          </div>

          {/* Cross-link to dedicated Astoria page */}
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h4 className="font-bold text-slate-900 mb-1">Looking for more Astoria-specific info?</h4>
              <p className="text-slate-700 text-sm">See our full guide to buying used cars in Astoria — local market insights, popular vehicles, and financing options.</p>
            </div>
            <Link
              to="/used-cars-astoria-oregon"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-sm"
            >
              Astoria Buyer Guide <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <NearbyAreasSection cityName="Astoria" nearbyAreas={nearbyAreas} />
    </>
  );
}

export { AstoriaServiceAreaPage };